import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, addDoc, query, where, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RacerRegistration, { RacerData } from '../../components/RacerRegistration';
import Navigation from '../../components/Navigation';
import { SettingsData, TimeSlot, UserProfileData, ServiceType } from '../../types/types';
import { defaultDays } from '../../utils';
import Calendar from '../../components/Calendar';
import AvailableTimes from '../../components/AvailableTimes';
import CountSelect from '../../components/CountSelect';

interface NewBookingProps {
  title: string;
  question: string;
  description: string;
  availableItems: "availableKarts" | "availableSimulators" | "availablePlayhouses" | "availableVr";
  serviceType: ServiceType;
  durationMinutes: "gocartSlotDurationMinutes" | "simulatorSlotDurationMinutes" | "playhouseSlotDurationMinutes" | "vrSlotDurationMinutes"
  intervalMinutes: "gocartSlotIntervalMinutes" | "simulatorSlotIntervalMinutes" | "playhouseSlotIntervalMinutes" | "vrSlotIntervalMinutes"
}

const NewBooking: React.FC<NewBookingProps> = ({ title, question, description, availableItems, serviceType, durationMinutes, intervalMinutes }) => {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [desiredItemCount, setDesiredItemCount] = useState<number>(1);

  const [settings, setSettings] = useState<SettingsData>({
    gocartSlotDurationMinutes: 8, // Alapértelmezett futam hossz: 8 perc
    gocartSlotIntervalMinutes: 15, // Alapértelmezett időköz: 15 perc
    simulatorSlotIntervalMinutes: 15,
    playhouseSlotIntervalMinutes: 15,
    vrSlotIntervalMinutes: 15,
    schedule: defaultDays,
    privateBlocks: [],
    availableKarts: 8,
    availableSimulators: 3,
    availablePlayhouses: 4,
    availableVr: 6,
    simulatorSlotDurationMinutes: 15,
    playhouseSlotDurationMinutes: 15,
    vrSlotDurationMinutes: 15,
    apiAccessToken: ''
  });

  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [profileCheckDone, setProfileCheckDone] = useState(false);
  const [availableItemsForSlot, setAvailableItemsForSlot] = useState<number[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [showRacerRegistration, setShowRacerRegistration] = useState(false);
  const [racers, setRacers] = useState<RacerData[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { currentUser, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  // Elérhető gokartok a beállítások alapján
  const allItems = useMemo(() =>
    Array.from({ length: settings[availableItems] }, (_, i) => i + 1),
    [settings[availableItems]]
  );

  // Lehetséges gokart számok a foglaláshoz
  const possibleItemCounts = useMemo(() =>
    Array.from({ length: settings[availableItems] }, (_, i) => i + 1),
    [settings[availableItems]]
  );

  // Adjunk hozzá egy új state-et a foglalások tárolására
  const [bookingsForDate, setBookingsForDate] = useState<any[]>([]);

  useEffect(() => {
    // Felhasználói profil ellenőrzése
    const checkUserProfile = async () => {
      if (currentUser) {
        try {
          const profileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));

          if (profileDoc.exists()) {
            const profileData = profileDoc.data() as UserProfileData;
            setIsProfileComplete(profileData.isProfileComplete || false);
          } else {
            setIsProfileComplete(false);
          }
        } catch (error) {
          console.error('Hiba a profil ellenőrzése során:', error);
          setIsProfileComplete(false);
        } finally {
          setProfileCheckDone(true);
        }
      } else {
        setProfileCheckDone(true);
      }
    };

    checkUserProfile();
  }, [currentUser]);

  useEffect(() => {
    // Beállítások lekérése
    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'bookings'));
        if (settingsDoc.exists()) {
          const settingsData = settingsDoc.data() as SettingsData;
          setSettings(settingsData);
        }
      } catch (error) {
        console.error('Hiba a beállítások lekérése során:', error);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    generateTimeSlots();
  }, [selectedDate, settings]);



  const getDayOfWeek = (dateString: string): keyof SettingsData['schedule'] => {
    const days: (keyof SettingsData['schedule'])[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const date = new Date(dateString);
    return days[date.getDay()];
  };

  const generateTimeSlots = async () => {
    setLoading(true);
    try {
      const dayOfWeek = getDayOfWeek(selectedDate);
      const daySchedule = settings.schedule[dayOfWeek];
      const now = new Date();
      const isToday = selectedDate === now.toISOString().split('T')[0];

      // Ha a nap zárva van, üres tömböt adunk vissza
      if (!daySchedule.isOpen) {
        setTimeSlots([]);
        setAvailableSlots([]);
        return;
      }

      // Csak a szolgáltatáshoz tartozó zártkörű eseményeket szűrjük
      const selectedDateStr = selectedDate.split('T')[0];
      const privateBlocksForDay = settings.privateBlocks.filter(block => {
        if (!block.date) return false;
        try {
          const blockDate = new Date(block.date);
          if (isNaN(blockDate.getTime())) return false;
          return blockDate.toISOString().split('T')[0] === selectedDateStr &&
            block.serviceType === serviceType;
        } catch (error) {
          console.error("Invalid date in privateBlocks:", block.date);
          return false;
        }
      });

      // Időpontok generálása a napi nyitvatartás alapján
      const slots: TimeSlot[] = [];

      const startDate = new Date(selectedDate);
      startDate.setHours(daySchedule.openingHour, 0, 0, 0);

      const endDate = new Date(selectedDate);
      endDate.setHours(daySchedule.closingHour, 0, 0, 0);

      let currentTime = new Date(startDate);

      while (currentTime < endDate) {
        const currentHour = currentTime.getHours();
        const currentMinutes = currentTime.getMinutes();

        // Futam vége időpont kiszámítása
        const endTimeObj = new Date(currentTime);
        endTimeObj.setMinutes(currentMinutes + settings[durationMinutes]);
        const endHour = endTimeObj.getHours();
        const endMinutes = endTimeObj.getMinutes();

        // Ellenőrizzük, hogy az időpont vagy annak vége nem esik-e zártkörű esemény idejére
        const isPrivateTime = privateBlocksForDay.some(block => {
          const slotStartTime = currentHour + (currentMinutes / 60);
          const slotEndTime = endHour + (endMinutes / 60);
          const isStartInPrivateBlock = slotStartTime >= block.startHour && slotStartTime < block.endHour;
          const isEndInPrivateBlock = slotEndTime > block.startHour && slotEndTime <= block.endHour;
          const doesSpanPrivateBlock = slotStartTime <= block.startHour && slotEndTime >= block.endHour;

          return isStartInPrivateBlock || isEndInPrivateBlock || doesSpanPrivateBlock;
        });

        // Ellenőrizzük, hogy az időpont nem korábbi-e mint a jelenlegi idő
        const isPastTime = isToday && (
          currentTime < now || // Teljes dátum összehasonlítás
          currentTime.getTime() <= now.getTime() // Vagy milliszekundumok összehasonlítása
        );

        if (!isPrivateTime) {
          const startTimeStr = currentTime.toLocaleTimeString('hu-HU', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });

          const endTimeStr = endTimeObj.toLocaleTimeString('hu-HU', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });

          slots.push({
            id: `${startTimeStr}-${endTimeStr}`,
            startTime: startTimeStr,
            endTime: endTimeStr,
            available: !isPastTime && true, // Ha múltbeli időpont, akkor nem elérhető
            isPrivateTime: false
          });
        } else {
          const startTimeStr = currentTime.toLocaleTimeString('hu-HU', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });

          const endTimeStr = endTimeObj.toLocaleTimeString('hu-HU', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });

          slots.push({
            id: `${startTimeStr}-${endTimeStr}`,
            startTime: startTimeStr,
            endTime: endTimeStr,
            available: false,
            isPrivateTime: true
          });
        }

        // Következő időpont
        currentTime.setMinutes(currentMinutes + settings[intervalMinutes]);
      }

      // Lekérjük a foglalásokat a kiválasztott napra
      const selectedDateObj = new Date(selectedDate);
      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);

      const bookingsRef = collection(db, 'bookings');
      const q = query(
        bookingsRef,
        where('date', '>=', Timestamp.fromDate(selectedDateObj)),
        where('date', '<', Timestamp.fromDate(nextDay)),
        where('bookingType', '==', serviceType)
      );

      const bookingsSnapshot = await getDocs(q);
      const bookings = bookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBookingsForDate(bookings);

      setTimeSlots(slots);
      // Azonnal szűrjük is az időpontokat a jelenlegi darabszám alapján
      filterAvailableTimeSlots(slots, bookings);
    } catch (error) {
      console.error('Hiba az időpontok generálása során:', error);
    } finally {
      setLoading(false);
    }
  };

  // Módosítsuk a filterAvailableTimeSlots függvényt
  const filterAvailableTimeSlots = (slots = timeSlots, bookings = bookingsForDate) => {
    if (!slots.length || !desiredItemCount) return;

    const filteredSlots = slots.map(slot => {
      const bookedItems = bookings
        .filter(booking =>
          booking.startTime === slot.startTime &&
          booking.status !== 'cancelled' // Csak a nem lemondott foglalásokat számoljuk
        )
        .map(booking => booking.itemNumber);

      const availableCount = allItems.length - bookedItems.length;

      return {
        ...slot,
        available: availableCount >= desiredItemCount,
        availableCount: availableCount
      };
    });

    setAvailableSlots(filteredSlots);
  };

  // Új segédfüggvény az elérhető helyek ellenőrzésére
  const checkAvailabilityForSlot = (slotStartTime: string, requiredCount: number) => {
    const bookedItems = bookingsForDate
      .filter(booking =>
        booking.startTime === slotStartTime &&
        booking.status !== 'cancelled' // Itt is csak az aktív foglalásokat vesszük figyelembe
      )
      .map(booking => booking.itemNumber);

    const availableCount = allItems.length - bookedItems.length;
    const available = allItems.filter(item => !bookedItems.includes(item));

    return {
      hasEnoughSpace: availableCount >= requiredCount,
      availableCount,
      availableItems: available
    };
  };

  // Módosított checkAvailableItems függvény
  const checkAvailableItems = (slotId: string) => {
    if (!slotId) return;

    const selectedSlotObj = timeSlots.find(slot => slot.id === slotId);
    if (!selectedSlotObj) return;

    const { hasEnoughSpace, availableItems } = checkAvailabilityForSlot(
      selectedSlotObj.startTime,
      desiredItemCount
    );

    setAvailableItemsForSlot(availableItems);

    if (hasEnoughSpace) {
      const itemsToSelect = availableItems.slice(0, desiredItemCount);
      setSelectedItems(itemsToSelect);
    } else {
      setSelectedSlots(prev => prev.filter(id => id !== slotId));
      setSelectedItems([]);
      alert(`Nincs elég szabad eszköz (${availableItems.length} db elérhető, ${desiredItemCount} db szükséges)`);
    }
  };

  // Módosítsuk a handleItemCountChange függvényt
  const handleItemCountChange = (count: number) => {
    setDesiredItemCount(count);

    const updatedSlots = timeSlots.map(slot => {
      const bookedItems = bookingsForDate
        .filter(booking =>
          booking.startTime === slot.startTime &&
          booking.status !== 'cancelled' // Itt is csak az aktív foglalásokat vesszük figyelembe
        )
        .map(booking => booking.itemNumber);

      const availableCount = allItems.length - bookedItems.length;

      return {
        ...slot,
        available: availableCount >= count,
        availableCount: availableCount
      };
    });

    setAvailableSlots(updatedSlots);

    // Ellenőrizzük a kiválasztott időpontokat
    if (selectedSlots.length > 0) {
      const invalidSlots: string[] = [];

      selectedSlots.forEach(slotId => {
        const slot = timeSlots.find(s => s.id === slotId);
        if (slot) {
          const bookedItems = bookingsForDate
            .filter(booking =>
              booking.startTime === slot.startTime &&
              booking.status !== 'cancelled' // Itt is csak az aktív foglalásokat vesszük figyelembe
            )
            .map(booking => booking.itemNumber);

          const availableCount = allItems.length - bookedItems.length;

          if (availableCount < count) {
            invalidSlots.push(slotId);
          }
        }
      });

      if (invalidSlots.length > 0) {
        setSelectedSlots(prev => prev.filter(id => !invalidSlots.includes(id)));
        setSelectedItems([]);
        alert(`Nem áll rendelkezésre ${count} szabad eszköz.`);
      } else if (selectedSlots.length === 1) {
        // Ha maradt egy kiválasztott időpont, frissítjük az eszközöket
        const slot = timeSlots.find(s => s.id === selectedSlots[0]);
        if (slot) {
          const { availableItems } = checkAvailabilityForSlot(slot.startTime, count);
          const itemsToSelect = availableItems.slice(0, count);
          setSelectedItems(itemsToSelect);
        }
      }
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    setSelectedSlots([]);
    setSelectedItems([]);
    setAvailableItemsForSlot([]);
  };

  const handleInvalidTimeSelect = () => {
    alert("Nem foglalhatsz korábbi időpontra, mint a jelenlegi időpont!");
  };

  const handleSlotSelect = (slotId: string) => {
    const selectedSlot = timeSlots.find(slot => slot.id === slotId);
    if (!selectedSlot) return;

    // Ellenőrizzük az időpontot
    const now = new Date();
    const [hours, minutes] = selectedSlot.startTime.split(':').map(Number);
    const slotDateTime = new Date(selectedDate);
    slotDateTime.setHours(hours, minutes, 0, 0);

    // Ha múltbeli időpont, nem engedjük a foglalást
    if (slotDateTime <= now) {
      handleInvalidTimeSelect();
      return;
    }

    setSelectedSlots(prev => {
      if (prev.includes(slotId)) {
        return prev.filter(id => id !== slotId);
      }
      return [...prev, slotId].sort((a, b) => {
        const timeA = timeSlots.find(slot => slot.id === a)?.startTime || '';
        const timeB = timeSlots.find(slot => slot.id === b)?.startTime || '';
        return timeA.localeCompare(timeB);
      });
    });
    checkAvailableItems(slotId);
  };

  // Módosított processBooking függvény
  const processBooking = async (racersList?: RacerData[]) => {
    try {
      if (selectedSlots.length === 0 || !currentUser) return;

      // Lekérjük a felhasználó profilját
      const userProfileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
      const userProfile = userProfileDoc.data();
      const userRacerId = userProfile?.racerId;

      // Foglalások létrehozása
      const bookingPromises = selectedSlots.flatMap(slotId => {
        const selectedSlotObj = timeSlots.find(slot => slot.id === slotId);
        if (!selectedSlotObj) return [];

        return selectedItems.map((itemNumber, index) => {
          // Ha van racersList, akkor abból vesszük az adatokat
          // Ha nincs és egy személyes foglalás van (profil kitöltve), akkor a felhasználó adatait használjuk
          const racerData = racersList && racersList[index % racersList.length]
            ? racersList[index % racersList.length]
            : {
              fullName: currentUser.displayName || 'Névtelen felhasználó',
              racerId: userRacerId  // A felhasználó racerId-ját használjuk
            };

          const bookingData = {
            userName: racerData.fullName || currentUser.displayName || 'Névtelen felhasználó',
            userEmail: currentUser.email,
            userId: currentUser.uid,
            itemNumber,
            date: Timestamp.fromDate(new Date(selectedDate)),
            startTime: selectedSlotObj.startTime,
            endTime: selectedSlotObj.endTime,
            status: 'pending' as const,
            bookingType: serviceType,
            racerId: racerData.racerId,
            createdAt: Timestamp.now()
          };

          return addDoc(collection(db, 'bookings'), bookingData);
        });
      });

      await Promise.all(bookingPromises);

      // Állapot frissítése
      setBookingSuccess(true);
      setSelectedSlots([]);
      setSelectedItems([]);
      setAvailableItemsForSlot([]);
      setShowRacerRegistration(false);

      // Azonnal újrageneráljuk az időpontokat és frissítjük az elérhetőséget
      await generateTimeSlots();

      // 10 másodperc múlva eltüntetjük a sikeres foglalás üzenetet
      setTimeout(() => {
        setBookingSuccess(false);
      }, 10000);
    } catch (error) {
      console.error('Hiba a foglalás során:', error);
      throw error;
    }
  };

  const handleBooking = async (): Promise<void> => {
    if (!currentUser) {
      return;
    }

    if (selectedSlots.length === 0) {
      return;
    }

    try {
      // Ellenőrizzük az elérhetőséget minden kiválasztott időpontra
      for (const slotId of selectedSlots) {
        const selectedSlotObj = timeSlots.find(slot => slot.id === slotId);
        if (!selectedSlotObj) continue;

        // Lekérjük az aktuális foglalásokat az adott időpontra
        const selectedDateObj = new Date(selectedDate);
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);

        const bookingsRef = collection(db, 'bookings');
        const q = query(
          bookingsRef,
          where('date', '>=', Timestamp.fromDate(selectedDateObj)),
          where('date', '<', Timestamp.fromDate(nextDay)),
          where('bookingType', '==', serviceType),
          where('startTime', '==', selectedSlotObj.startTime),
          where('status', '!=', 'cancelled')  // Csak az aktív foglalásokat számoljuk
        );

        const bookingsSnapshot = await getDocs(q);
        const currentBookings = bookingsSnapshot.docs.length;

        // Ellenőrizzük, hogy van-e elég hely
        if (currentBookings + desiredItemCount > settings[availableItems]) {
          alert(`A ${selectedSlotObj.startTime} időpontra már nincs elegendő szabad eszköz (${desiredItemCount} db). Az elérhető időpontok frissítéséhez kattintson az oldal frissítése gombra.`);
          return;
        }
      }

      // Ha a profil nincs kitöltve, vagy több gokartot foglalunk, megjelenítjük a vendég regisztrációs komponenst
      if (!isProfileComplete || desiredItemCount > 1) {
        setShowRacerRegistration(true);
        return;
      }

      // Ha minden rendben, folytatjuk a foglalást
      await processBooking();
    } catch (error) {
      console.error('Hiba a foglalás során:', error);
      alert("Hiba történt a foglalás során. Kérjük, próbáld újra!");
    }
  };

  // Vendég regisztráció befejezése
  const handleRacerRegistrationComplete = async (racersList: RacerData[]) => {
    setRacers(racersList);
    setShowRacerRegistration(false);

    try {
      // Az első vendég adatait használjuk a felhasználói profilhoz
      if (racersList.length > 0) {
        setIsProfileComplete(racersList[0].isProfileComplete);
      }

      // Újra ellenőrizzük az időpontok elérhetőségét
      for (const slotId of selectedSlots) {
        const selectedSlotObj = timeSlots.find(slot => slot.id === slotId);
        if (!selectedSlotObj) continue;

        // Lekérjük a legfrissebb foglalásokat az adott időpontra
        const selectedDateObj = new Date(selectedDate);
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);

        const bookingsRef = collection(db, 'bookings');
        const q = query(
          bookingsRef,
          where('date', '>=', Timestamp.fromDate(selectedDateObj)),
          where('date', '<', Timestamp.fromDate(nextDay)),
          where('bookingType', '==', serviceType),
          where('startTime', '==', selectedSlotObj.startTime),
          where('status', '!=', 'cancelled')  // Csak az aktív foglalásokat számoljuk
        );

        const bookingsSnapshot = await getDocs(q);
        const currentBookings = bookingsSnapshot.docs.length;

        // Ellenőrizzük, hogy van-e elég hely
        if (currentBookings + desiredItemCount > settings[availableItems]) {
          alert(`Figyelem! Az időközben történt foglalások miatt a ${selectedSlotObj.startTime} időpontra már nincs elegendő szabad eszköz (${desiredItemCount} db). Kérjük, válassz más időpontot.`);
          // Frissítjük az időpontokat
          await generateTimeSlots();
          return;
        }
      }

      // Folytatjuk a foglalást a vendég adatokkal
      await processBooking(racersList);
    } catch (error) {
      console.error('Hiba a foglalás során:', error);
      alert("Hiba történt a foglalás során. Kérjük, próbáld újra!");
    }
  };

  // Vendég regisztráció megszakítása
  const handleRacerRegistrationCancel = () => {
    setShowRacerRegistration(false);
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    };
    return new Date(dateString).toLocaleDateString('hu-HU', options);
  };

  // Ha a felhasználó be van jelentkezve, de nincs kitöltve a profilja, átirányítjuk a profil oldalra
  useEffect(() => {
    if (currentUser && profileCheckDone && !isProfileComplete && selectedSlots.length > 0) {
      navigate('/profile');
    }
  }, [currentUser, profileCheckDone, isProfileComplete, selectedSlots, navigate]);

  // Kilépés kezelése
  const handleLogout = async () => {
    if (window.confirm('Biztosan ki szeretnél jelentkezni?')) {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('Hiba a kijelentkezés során:', error);
      }
    }
  };

  // Mobil menü megnyitása/bezárása
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Ellenőrizzük, hogy az adott nap nyitva van-e
  const isDayOpen = settings.schedule[getDayOfWeek(selectedDate)].isOpen;

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation showPublicBooking={true} />

      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white overflow-hidden shadow-sm rounded-lg">
            <div className="p-6 bg-white border-b border-gray-200">
              <h1 className="text-2xl font-semibold text-gray-900 mb-6 text-center">{title}</h1>

              {bookingSuccess && (
                <div className="mb-6 p-4 bg-green-100 text-green-800 rounded-lg shadow-md border border-green-200 flex items-center">
                  <div>
                    <span className="font-medium">A foglalás sikeres!</span>
                  </div>
                </div>
              )}

              {currentUser && !isProfileComplete && !showRacerRegistration && (
                <div className="mb-6 p-4 bg-yellow-100 text-yellow-800 rounded-lg shadow-md border border-yellow-200 flex items-center">
                  <div>
                    A foglaláshoz ki kell töltened a profilodat. Kérjük, kattints a <span className="font-medium">"Profil"</span> gombra a navigációs sávban.
                  </div>
                </div>
              )}

              {showRacerRegistration ? (
                <RacerRegistration
                  userId={currentUser?.uid || ''}
                  itemCount={desiredItemCount}
                  onComplete={handleRacerRegistrationComplete}
                  onCancel={handleRacerRegistrationCancel}
                  isAdmin={isAdmin}
                />
              ) : (
                <>
                  <Calendar
                    selectedDate={selectedDate}
                    handleDateChange={handleDateChange}
                    formatDate={formatDate}
                    isDayOpen={isDayOpen}
                    settings={settings}
                    getDayOfWeek={getDayOfWeek}
                    type={serviceType}
                  />

                  {isDayOpen && (
                    <>
                      <div className="my-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">{question}</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          {description}
                        </p>

                        <CountSelect
                          possibleCounts={possibleItemCounts}
                          desiredCount={desiredItemCount}
                          onCountChange={handleItemCountChange}
                        />
                      </div>

                      {desiredItemCount > 0 && (
                        <AvailableTimes
                          settings={settings}
                          selectedDate={selectedDate}
                          loading={loading}
                          availableSlots={availableSlots}
                          selectedSlots={selectedSlots}
                          getDayOfWeek={getDayOfWeek}
                          handleSlotSelect={handleSlotSelect}
                          availableItems={availableItems}
                          onInvalidTimeSelect={handleInvalidTimeSelect}
                        />
                      )}
                      {selectedSlots.length > 0 && (
                        <div id="bookingSummarySection" className="mt-6">
                          <div className="bg-gray-50 p-4 rounded-md shadow-sm mb-6 border border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                              Foglalási összesítő
                            </h3>
                            <div className="space-y-2">
                              <div className="flex items-center">
                                <span className="text-sm text-gray-600 mr-2">Dátum:</span>
                                <span className="font-medium">{formatDate(selectedDate)}</span>
                              </div>
                              <div className="flex items-center">
                                <span className="text-sm text-gray-600 mr-2">Időpontok:</span>
                                <span className="font-medium">
                                  {selectedSlots
                                    .map(slotId => timeSlots.find(slot => slot.id === slotId)?.startTime)
                                    .filter((time): time is string => time !== undefined)
                                    .sort()
                                    .join(', ')}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <span className="text-sm text-gray-600 mr-2">Eszközök:</span>
                                <span className="font-medium">{desiredItemCount} db</span>
                              </div>
                              <div className="flex items-center">
                                <span className="text-sm text-gray-600 mr-2">Összes foglalás:</span>
                                <span className="font-medium">{selectedSlots.length * desiredItemCount} db</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Foglalás gomb */}
                      <div className="mt-4 flex justify-end">
                        {selectedSlots.length > 0 ? (
                          <button
                            onClick={handleBooking}
                            disabled={!currentUser}
                            className={`px-5 py-2 rounded-md shadow-sm transition-all duration-200 flex items-center ${!currentUser
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-red-600 text-white hover:bg-red-700 transform hover:scale-105'
                              }`}
                          >
                            {!currentUser ? (
                              <>Bejelentkezés szükséges</>
                            ) : (
                              <>Foglalás megerősítése ({selectedSlots.length * desiredItemCount} foglalás)</>
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => document.getElementById('timeSlotSection')?.scrollIntoView({ behavior: 'smooth' })}
                            disabled={desiredItemCount === 0}
                            className={`px-4 py-2 rounded-md shadow-sm transition-all duration-200 flex items-center ${desiredItemCount === 0
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                          >
                            <span>Tovább</span>
                          </button>
                        )}
                      </div>

                      {!currentUser && isDayOpen && (
                        <div className="mt-4 p-4 bg-yellow-50 text-yellow-800 rounded-lg shadow-md border border-yellow-200 flex items-center">
                          <div>
                            A foglaláshoz be kell jelentkezned. Kérjük, kattints a <span className="font-medium">"Bejelentkezés"</span> gombra.
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBooking; 